import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {Autocomplete, Box, Button, Chip, Link, TextField} from '@mui/material';
import {MRT_ColumnDef, MRT_SortingState} from 'material-react-table';

import {EventStatus, formatEventStatus} from '@entities/event';
import {Topic, useGetTopics} from '@entities/topic';
import {MrTable} from '@shared/components';
import {
  CategoryFilter,
  FilterRow,
  SearchFilter,
  SelectFilter,
  SubCategoryFilter,
} from '@shared/components/filters';
import {
  DATETIME_FORMAT,
  entityTableCommonProps,
  LIVE_STATUS_FILTER,
  ROUTE__CREATE_TOPIC,
  ROUTE__TOPIC_DETAILS,
} from '@shared/constants';
import {
  getStoredCategoryId,
  storeSharedCategoryId,
  useUrlState,
} from '@shared/hooks';
import {formatInTz} from '@shared/utils';

import {PaginationState} from '@/shared/types';

const localStorageKey = 'topicsTable';

const EVENT_STATUS_OPTIONS = Object.values(EventStatus).map(s => ({
  value: s,
  label: formatEventStatus(s),
}));

type UrlState = PaginationState & {
  isLive?: boolean | null;
  categoryId: string | null;
  subCategoryId: string | null;
  searchQuery?: string;
  sortField: string;
  sortOrder: 'asc' | 'desc';
  eventStatuses: EventStatus[];
};

const columns: MRT_ColumnDef<Topic>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    size: 300,
    enableSorting: false,
    Cell: ({row}) => {
      const {topicId, logoUrl, name} = row.original;
      const displayName = name || topicId;

      return (
        <Box display="flex" alignItems="center" gap={1}>
          {logoUrl && (
            <img
              src={logoUrl}
              width={28}
              height={28}
              alt={displayName}
              style={{borderRadius: 6, objectFit: 'cover', flexShrink: 0}}
            />
          )}
          <Link
            component={RouterLink}
            to={ROUTE__TOPIC_DETAILS.replace(':topic_id', topicId)}
          >
            {displayName}
          </Link>
        </Box>
      );
    },
  },
  {
    accessorKey: 'categoryName',
    header: 'Category',
    size: 200,
    enableSorting: false,
  },
  {
    accessorKey: 'subCategories',
    header: 'Subcategory',
    size: 200,
    enableSorting: false,
    Cell: ({row}) => row.original.subCategories?.[0]?.name ?? null,
  },
  {
    accessorKey: 'publishedEventsCount',
    header: 'Published events',
    size: 160,
    enableSorting: false,
    Cell: ({row}) => row.original.publishedEventsCount ?? 0,
  },
  {
    accessorKey: 'startDate',
    header: 'Start date',
    size: 160,
    Cell: ({row}) => {
      const date = row.original.startDate;
      if (!date) return null;
      return formatInTz(date, DATETIME_FORMAT);
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    size: 160,
    Cell: ({row}) => {
      const date = row.original.createdAt;
      if (!date) return null;
      return formatInTz(date, DATETIME_FORMAT);
    },
  },
];

export const TopicsPage = () => {
  const navigate = useNavigate();

  const {urlState, setUrl} = useUrlState<UrlState>({
    pageIndex: 0,
    pageSize: 10,
    isLive: null,
    categoryId: getStoredCategoryId(),
    subCategoryId: null,
    searchQuery: undefined,
    sortField: 'created_at',
    sortOrder: 'desc',
    eventStatuses: [],
  });

  const sortFieldToColumnId: Record<string, string> = {
    created_at: 'createdAt',
    start_date: 'startDate',
  };
  const columnIdToSortField: Record<string, string> = {
    createdAt: 'created_at',
    startDate: 'start_date',
  };

  const sorting: MRT_SortingState = [
    {
      id: sortFieldToColumnId[urlState.sortField] ?? 'createdAt',
      desc: urlState.sortOrder === 'desc',
    },
  ];

  const {data, isPending, error} = useGetTopics({
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
    searchQuery: urlState.searchQuery ?? undefined,
    isLive: urlState.isLive ?? undefined,
    categoryId: urlState.categoryId ?? undefined,
    subCategoryIds: urlState.subCategoryId ?? undefined,
    sortField: urlState.sortField,
    sortOrder: urlState.sortOrder,
    eventStatuses:
      urlState.eventStatuses.length > 0 ? urlState.eventStatuses : undefined,
  });

  return (
    <MrTable
      error={error}
      columns={columns}
      data={data?.items ?? []}
      renderTopToolbarCustomActions={() => {
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            gap={2}
          >
            <Button
              onClick={() => {
                navigate(ROUTE__CREATE_TOPIC);
              }}
            >
              Create topic
            </Button>
            <FilterRow>
              <SearchFilter
                value={urlState.searchQuery}
                onChange={searchQuery =>
                  setUrl(prev => ({
                    ...prev,
                    pageIndex: 0,
                    searchQuery,
                  }))
                }
              />

              <CategoryFilter
                size="small"
                value={urlState.categoryId}
                sx={{minWidth: 200}}
                onChange={categoryId => {
                  storeSharedCategoryId(categoryId);
                  setUrl(prev => ({
                    ...prev,
                    pageIndex: 0,
                    categoryId,
                    subCategoryId: null,
                  }));
                }}
              />

              <SubCategoryFilter
                size="small"
                categoryId={urlState.categoryId}
                value={urlState.subCategoryId}
                sx={{minWidth: 200}}
                onChange={subCategoryId =>
                  setUrl(prev => ({
                    ...prev,
                    pageIndex: 0,
                    subCategoryId,
                  }))
                }
              />

              <SelectFilter
                value={urlState.isLive ?? null}
                onChange={isLive =>
                  setUrl(prev => ({
                    ...prev,
                    pageIndex: 0,
                    isLive,
                  }))
                }
                options={LIVE_STATUS_FILTER.options}
              />

              <Autocomplete
                multiple
                size="small"
                options={EVENT_STATUS_OPTIONS}
                value={EVENT_STATUS_OPTIONS.filter(o =>
                  urlState.eventStatuses.includes(o.value),
                )}
                getOptionLabel={o => o.label}
                onChange={(_, selected) =>
                  setUrl(prev => ({
                    ...prev,
                    pageIndex: 0,
                    eventStatuses: selected.map(o => o.value),
                  }))
                }
                renderInput={params => (
                  <TextField {...params} placeholder="Event status" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({index})}
                      key={option.value}
                      label={option.label}
                      size="small"
                    />
                  ))
                }
                sx={{minWidth: 200}}
                disableCloseOnSelect
              />
            </FilterRow>
          </Box>
        );
      }}
      localStorageKeyForSettings={localStorageKey}
      enableHiding
      /////////////////////////////
      // pagination + state
      enablePagination
      manualPagination
      onPaginationChange={updater =>
        setUrl(prev => {
          const nextPagination =
            typeof updater === 'function' ? updater(prev) : updater;

          return {
            ...prev,
            ...nextPagination,
          };
        })
      }
      rowCount={data?.total ?? 0}
      /////////////////////////////
      manualSorting
      onSortingChange={updater => {
        const nextSorting =
          typeof updater === 'function' ? updater(sorting) : updater;
        const first = nextSorting[0];
        setUrl(prev => ({
          ...prev,
          pageIndex: 0,
          sortField: first
            ? (columnIdToSortField[first.id] ?? 'created_at')
            : 'created_at',
          sortOrder: first ? (first.desc ? 'desc' : 'asc') : 'desc',
        }));
      }}
      state={{
        isLoading: isPending,
        pagination: {
          pageIndex: urlState.pageIndex,
          pageSize: urlState.pageSize,
        },
        sorting,
      }}
      {...entityTableCommonProps}
      enableRowActions={false}
    />
  );
};
